import type { MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EventFilterView } from '@/features/EventFilterView';
import { usePaginatedEvents } from '@/features/EventsListLayout/hooks';
import { EngineService } from '@/services/engine';
import { hasRouterHistory } from '@/stores/navigation-history.store';
import { useDeletedItems, useOpenInNewWindow } from '@/hooks';
import { buildSourceEventsFeedContext } from '@/stores/event-feed.store';
import { EVENTS_CONFIG } from '../constants';
import type { EventsPageScenario } from '../types';
import { useEventsPageFilters } from '../useEventsPageFilters';
import { calculateSourceCount } from './calculateSourceCount';

export function useRecentEventsScenario(): EventsPageScenario {
    const { t } = useTranslation([
        I18nNamespace.COMMON,
        I18nNamespace.EVENTS,
        I18nNamespace.SOURCES,
    ]);
    const { sourceId, source, hasSourceFilter, sortOption } =
        useEventsPageFilters();
    const { openEvent } = useOpenInNewWindow();

    const eventsResult = usePaginatedEvents({
        sourceId,
        pageKey: 'recent-events',
        sortOption,
    });
    const pageStateKey = eventsResult.pageStateKey ?? 'recent-events';

    const items = useMemo(
        () => eventsResult.events.map((event) => ({ event })),
        [eventsResult.events],
    );

    const backButtonLabel = useMemo(() => {
        if (source?.displayName) {
            return t('page.backToSource', { name: source.displayName });
        }
        return undefined;
    }, [source?.displayName, t]);

    const onEventClick = useCallback(
        (clickedEventId: string, event?: MouseEvent) => {
            openEvent(clickedEventId, event, {
                state: {
                    from: sourceId ? 'source-events' : 'events',
                    feedContext: sourceId
                        ? buildSourceEventsFeedContext(sourceId, pageStateKey)
                        : { type: 'recentEvents' },
                },
            });
        },
        [openEvent, sourceId],
    );

    const { filteredItems, deletedCount } = useDeletedItems({
        items,
        getItemId: (item) => item.event.id,
        deleteItemsFn: (ids) => EngineService.deleteEvents(ids),
        successMessage: t('actions.deleteSuccess'),
        errorMessage: t('actions.deleteError'),
    });

    const sourceCount = useMemo(
        () => calculateSourceCount(filteredItems, eventsResult, sourceId),
        [
            filteredItems,
            eventsResult.totalItems,
            eventsResult.pageSize,
            sourceId,
        ],
    );

    return {
        pageKey: pageStateKey,
        items: filteredItems,
        eventCount: eventsResult.totalItems
            ? eventsResult.totalItems - deletedCount
            : filteredItems.length,
        error: eventsResult.error,
        isLoading: eventsResult.isLoading,
        pagination: {
            mode: 'paginated',
            page: eventsResult.page,
            pageSize: eventsResult.pageSize,
            totalCount: eventsResult.totalItems,
            isFetching: eventsResult.isFetching,
            onPageChange: eventsResult.onPageChange,
        },
        isEmpty: filteredItems.length === 0,
        sourceCount,
        emptyMessage: t('events:page.noResults'),
        showBackButton: hasSourceFilter || hasRouterHistory(),
        backButtonLabel,
        filterView: hasSourceFilter ? (
            <EventFilterView config={EVENTS_CONFIG} />
        ) : undefined,
        onEventClick,
    };
}
